import React, { useState, useRef, useEffect } from "react";

type Props = {
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  className?: string;
  maxLength?: number;
  autoComplete?: string;
  id?: string;
  name?: string;
  inputMode?: "text" | "email" | "tel" | "url" | "numeric" | "decimal" | "search";
  enterKeyHint?: "enter" | "done" | "go" | "next" | "previous" | "search" | "send";
};

/**
 * iOS Safari対応強化版InputFieldコンポーネント
 * iPhone 13のtype="password"入力問題を解決するための最適化実装
 */
const InputFieldEnhanced = ({
  type = "text",
  placeholder,
  value,
  onChange,
  disabled = false,
  className,
  maxLength,
  autoComplete,
  id,
  name,
  inputMode,
  enterKeyHint,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // iOS Safari detection
  const isIOSSafari = () => {
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua);
    const webkit = /WebKit/.test(ua);
    const chrome = /CriOS|Chrome/.test(ua);
    return iOS && webkit && !chrome;
  };

  // Determine optimal input attributes for iOS Safari
  const getOptimalAttributes = () => {
    const isPassword = type === "password";
    const isIOSDevice = isIOSSafari();
    
    if (isPassword && isIOSDevice) {
      return {
        type: "password",
        autoComplete: autoComplete || "current-password",
        autoCapitalize: "none" as const,
        autoCorrect: "off",
        spellCheck: false,
        inputMode: "text" as const, // パスワードでもtextを指定
        enterKeyHint: enterKeyHint || "done" as const,
        // iOS Safari固有の問題回避
        "data-lpignore": "true", // LastPass干渉防止
        "data-form-type": "password",
      };
    }
    
    return {
      type,
      autoComplete,
      autoCapitalize: type === "email" ? "none" : undefined,
      autoCorrect: type === "email" ? "off" : undefined,
      spellCheck: type === "email" ? false : undefined,
      inputMode,
      enterKeyHint,
    };
  };

  // Handle focus with iOS-specific optimizations
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    setHasInteracted(true);
    
    // iOS Safari: Prevent zoom on focus
    if (isIOSSafari()) {
      e.target.style.fontSize = '16px';
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    
    // Restore original font size
    if (isIOSSafari()) {
      e.target.style.fontSize = '';
    }
  };

  // Enhanced touch handling for iOS
  const handleTouchStart = (e: React.TouchEvent<HTMLInputElement>) => {
    // Ensure the input gets focus on touch
    if (!isFocused && inputRef.current) {
      // Use setTimeout to ensure proper focus handling on iOS
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  // iOS Safari: Force re-render on value change to prevent stuck cursor
  useEffect(() => {
    if (isIOSSafari() && type === "password" && inputRef.current && value) {
      const input = inputRef.current;
      const cursorPosition = input.selectionStart;
      
      // Micro-delay to handle iOS rendering issues
      setTimeout(() => {
        if (cursorPosition !== null) {
          input.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
    }
  }, [value, type]);

  const attributes = getOptimalAttributes();
  
  return (
    <input
      {...attributes}
      ref={inputRef}
      id={id}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onTouchStart={handleTouchStart}
      disabled={disabled}
      className={`${className} ${isFocused ? 'input-focused' : ''} ${hasInteracted ? 'input-interacted' : ''}`}
      maxLength={maxLength}
      style={{
        // iOS Safari最適化スタイル
        WebkitAppearance: 'none',
        // ズーム防止（16px以上でズームを無効化）
        fontSize: isIOSSafari() && isFocused ? '16px' : undefined,
        // タッチアクション最適化
        touchAction: 'manipulation',
        // レンダリング最適化
        transform: 'translateZ(0)', // ハードウェアアクセラレーション
        backfaceVisibility: 'hidden',
        // フォーカス時のスタイルリセット
        outline: 'none',
        // iOS Safari固有の問題回避
        WebkitUserSelect: 'text',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
      // iOS Safari: 追加のパスワード最適化属性
      {...(type === "password" && isIOSSafari() ? {
        'data-webkit-dummy-input': 'true',
        'data-password-field': 'true',
      } : {})}
    />
  );
};

export default InputFieldEnhanced;